/**
 * Production Permalinks & Comprehensive SEO Router for Thinkers Arcade
 * Emulates professional Yoast SEO / RankMath architecture:
 * - Focus Keyword Phrase
 * - SEO Title (30-60 characters)
 * - Meta Description (120-160 characters)
 * - OpenGraph & Twitter Cards
 * - Canonical Links
 * - Dynamic Schema.org JSON-LD (FAQPage + BreadcrumbList + Service / Product)
 */

export type AppView =
  | 'home'
  | 'solutions'
  | 'website-development'
  | 'app-development'
  | 'custom-erps'
  | 'ai-solutions'
  | 'seo-growth'
  | 'cybersecurity'
  | 'hosting'
  | 'domains'
  | 'pricing'
  | 'store'
  | 'case-studies'
  | 'insights'
  | 'industries'
  | 'venture-lab'
  | 'portal'
  | 'contact'
  | 'kenya-nairobi'
  | 'kenya-mombasa'
  | 'kenya-nakuru'
  | 'kenya-kisumu'
  | 'kenya-meru';

export interface RouteFaqItem {
  question: string;
  answer: string;
}

export interface RouteConfig {
  path: string;
  focusKeyword: string;
  seoTitle: string;
  metaDescription: string;
  ogTitle: string;
  ogDescription: string;
  canonicalUrl: string;
  schemaType: 'WebSite' | 'Service' | 'SoftwareApplication' | 'Product' | 'ContactPage' | 'CollectionPage' | 'AboutPage';
  wordCountTarget: string;
  faqs?: RouteFaqItem[];
}

export const ROUTE_MAP: Record<AppView, RouteConfig> = {
  home: {
    path: '/',
    focusKeyword: 'website development Kenya apps software hosting Nairobi',
    seoTitle: 'Thinkers Arcade | Digital Infrastructure & Systems Kenya',
    metaDescription: 'We design and scale high-speed websites, mobile apps, custom ERPs, and LiteSpeed hosting for ambitious businesses in Kenya. Launch with Lipa na M-Pesa today.',
    ogTitle: 'Thinkers Arcade | Digital Infrastructure & Systems Kenya',
    ogDescription: 'We design and scale high-speed websites, mobile apps, custom ERPs, and LiteSpeed hosting for ambitious businesses in Kenya. Launch with Lipa na M-Pesa today.',
    canonicalUrl: 'https://thinkersarcade.co.ke/',
    schemaType: 'WebSite',
    wordCountTarget: '1200-1500 words',
    // Per user instruction: "add FAQ but not on the home page"
    faqs: undefined,
  },

  solutions: {
    path: '/solutions/',
    focusKeyword: 'digital infrastructure solutions Kenya software development',
    seoTitle: 'Digital Infrastructure Solutions in Kenya | Thinkers Arcade',
    metaDescription: 'Explore our dedicated digital infrastructure verticals: Sub-0.8s web systems, native mobile apps, custom ERP software, AI automation, and NVMe hosting.',
    ogTitle: 'Digital Infrastructure Solutions in Kenya | Thinkers Arcade',
    ogDescription: 'Explore our dedicated digital infrastructure verticals: Sub-0.8s web systems, native mobile apps, custom ERP software, AI automation, and NVMe hosting.',
    canonicalUrl: 'https://thinkersarcade.co.ke/solutions/',
    schemaType: 'CollectionPage',
    wordCountTarget: '900-1200 words',
    faqs: [
      {
        question: 'What digital infrastructure verticals does Thinkers Arcade specialize in?',
        answer: 'We architect and deploy five interconnected technology verticals: 1) High-conversion websites and e-commerce platforms with Core Web Vitals under 0.8s, 2) iOS and Android mobile apps with Safaricom Daraja M-Pesa push, 3) Custom relational ERP systems replacing fragile Excel files, 4) AI automation agents and WhatsApp bots, and 5) Tier-4 NVMe LiteSpeed cloud hosting.',
      },
      {
        question: 'How do you determine whether a business needs a website, mobile app, or custom ERP?',
        answer: 'During our complimentary 45-minute architectural diagnostic, we assess your primary commercial bottleneck. Public client discovery and SEO require a high-speed web platform. Customer retention, push notifications, and field operations thrive on mobile apps. Multi-branch inventory, KRA eTIMS fiscal compliance, and staff accountability require a custom ERP database.',
      },
      {
        question: 'Can you integrate disparate systems like POS tills, M-Pesa Paybills, and KRA eTIMS?',
        answer: 'Yes. Every solution we build features unified API middleware connecting your Lipa na M-Pesa Paybill (542542 or custom), bank gateways, KRA eTIMS automated tax signing, and local SMS dispatch gateways.',
      },
      {
        question: 'Do you offer milestone-based payment plans like Lipa Pole Pole?',
        answer: 'Yes. All turnkey solutions qualify for our 3-part Lipa Pole Pole commercial plan: 40% project kickoff deposit, 30% upon interactive prototype and milestone review, and 30% upon live production deployment.',
      },
      {
        question: 'What regions across Kenya do your deployment teams support?',
        answer: 'We provide on-site engineering and remote deployment across Nairobi, Nakuru, Thika, Meru, Mombasa, Kiambu, Ruiru, Eldoret, and Kisumu with 99.9% uptime SLAs.',
      },
    ],
  },

  'website-development': {
    path: '/solutions/website-development/',
    focusKeyword: 'website development packages Kenya affordable web design Nairobi',
    seoTitle: 'Website Development Packages in Kenya | Thinkers Arcade',
    metaDescription: 'Official website packages from KES 9,000. Sub-0.8s Core Web Vitals on Safaricom 4G, Lipa na M-Pesa checkouts, and 19 live verified client deployments in Kenya.',
    ogTitle: 'Website Development Packages in Kenya | Thinkers Arcade',
    ogDescription: 'Official website packages from KES 9,000. Sub-0.8s Core Web Vitals on Safaricom 4G, Lipa na M-Pesa checkouts, and 19 live verified client deployments in Kenya.',
    canonicalUrl: 'https://thinkersarcade.co.ke/solutions/website-development/',
    schemaType: 'Service',
    wordCountTarget: '1200-1500 words',
    faqs: [
      {
        question: 'How much does website development cost in Kenya at Thinkers Arcade?',
        answer: 'Our official pricing starts at KES 9,000 for the Starter Business Package (delivered in 48 hours), KES 18,000 for the Professional Corporate Package (5-day turnaround), and KES 35,000 for full E-Commerce & Systems featuring automated Safaricom Daraja Lipa na M-Pesa checkout rails.',
      },
      {
        question: 'What is included in the KES 9,000 Starter Business Website package?',
        answer: 'The KES 9,000 package includes a fast single-page or multi-section landing page, free .co.ke domain registration for 1 year, free SSL security certificate, mobile-first responsive layout, WhatsApp direct chat button, Google Maps 3-Pack integration, and basic SEO meta configuration.',
      },
      {
        question: 'Why are Thinkers Arcade websites faster than standard WordPress sites in Kenya?',
        answer: 'We engineer our websites using headless React/Next.js architectures deployed onto LiteSpeed enterprise NVMe edge servers peered at the local Kenya Internet Exchange Point (KIXP). This achieves Largest Contentful Paint (LCP) under 0.8 seconds even on congested 3G/4G Safaricom mobile connections.',
      },
      {
        question: 'Can customers pay via Lipa na M-Pesa Paybill or Till directly on my website?',
        answer: 'Yes. Our e-commerce and business packages integrate Safaricom Daraja 2.0 API for automated STK push notifications, sending an instant PIN prompt directly to your customer phone and reconciling payments with your bank account.',
      },
      {
        question: 'Can I inspect real websites you have already built for Kenyan companies?',
        answer: 'Yes. We showcase 19 verified live production client websites including Jaymax Auto (jaymaxauto.co.ke), Shantis Designs (shantisdesigns.co.ke), Sprinkville Networks (sprinkvillenetworks.co.ke), Topsec Technologies (topsectechnologies.co.ke), Nairobi West Spa (nairobiwestspa.co.ke), and CK Law Advocates (cklaw.co.ke).',
      },
      {
        question: 'How does your Lipa Pole Pole installment payment plan work?',
        answer: 'For projects KES 18,000 and above, you can split your investment into 3 monthly installments or milestone payments, allowing your business to launch and start generating revenue before settling the final balance.',
      },
    ],
  },

  'app-development': {
    path: '/solutions/app-development/',
    focusKeyword: 'mobile app development Kenya Android iOS Flutter Nairobi',
    seoTitle: 'Custom Mobile App Development in Kenya | Thinkers Arcade',
    metaDescription: 'Native iOS, Android, and Flutter mobile apps with Safaricom Daraja M-Pesa STK push checkouts, offline SQLite sync, and Google Play Store launch in Kenya.',
    ogTitle: 'Custom Mobile App Development in Kenya | Thinkers Arcade',
    ogDescription: 'Native iOS, Android, and Flutter mobile apps with Safaricom Daraja M-Pesa STK push checkouts, offline SQLite sync, and Google Play Store launch in Kenya.',
    canonicalUrl: 'https://thinkersarcade.co.ke/solutions/app-development/',
    schemaType: 'Service',
    wordCountTarget: '1000-1300 words',
    faqs: [
      {
        question: 'How much does it cost to build a mobile app in Kenya?',
        answer: 'Mobile app development at Thinkers Arcade starts from KES 45,000 for specialized utility and single-purpose apps, and KES 85,000 to KES 150,000 for full-featured dual-platform (iOS and Android) applications with user authentication, database sync, and M-Pesa checkouts.',
      },
      {
        question: 'Which mobile frameworks do you use: Native or Cross-Platform?',
        answer: 'We build primarily with Flutter and React Native for unified cross-platform performance, reducing build costs and timeline by 40% while maintaining 60fps native UI responsiveness. For hardware-specific applications, we utilize Kotlin (Android) and Swift (iOS).',
      },
      {
        question: 'Can the mobile app work offline in areas with poor internet connection?',
        answer: 'Yes. We architect our apps with local SQLite/Room embedded databases. Field agents can record orders, inspect inventory, and collect data completely offline. All records synchronize automatically as soon as internet connectivity is restored.',
      },
      {
        question: 'Do you handle submission to the Google Play Store and Apple App Store?',
        answer: 'Yes. We manage end-to-end publishing including asset generation, privacy policy verification, Google Play 14-day closed testing compliance, and Apple App Store review approvals.',
      },
      {
        question: 'How is Lipa na M-Pesa integrated into mobile apps?',
        answer: 'We connect directly to Safaricom Daraja API. When the user taps Pay, an M-Pesa STK push prompt is triggered instantly on their SIM, allowing them to authorize the transaction via PIN without leaving your app.',
      },
    ],
  },

  'custom-erps': {
    path: '/solutions/custom-erps/',
    focusKeyword: 'custom ERP software Kenya Excel spreadsheet conversion Nairobi',
    seoTitle: 'Custom ERP & Software Development Kenya | Thinkers Arcade',
    metaDescription: 'Transform fragile Excel sheets into multi-user PostgreSQL apps. Real-time warehouse inventory, multi-branch POS, and automated KRA eTIMS tax integration.',
    ogTitle: 'Custom ERP & Software Development Kenya | Thinkers Arcade',
    ogDescription: 'Transform fragile Excel sheets into multi-user PostgreSQL apps. Real-time warehouse inventory, multi-branch POS, and automated KRA eTIMS tax integration.',
    canonicalUrl: 'https://thinkersarcade.co.ke/solutions/custom-erps/',
    schemaType: 'SoftwareApplication',
    wordCountTarget: '1000-1400 words',
    faqs: [
      {
        question: 'Why should a Kenyan business convert Excel spreadsheets into a custom ERP?',
        answer: 'Excel spreadsheets lack multi-user concurrencies, audit trails, and role-based security. Formula overwrites, accidental deletions, and offline desynchronization cause severe inventory and cash leaks. A custom ERP gives every branch live centralized stock counts, automated POS receipts, and immutable audit logs.',
      },
      {
        question: 'How long does an Excel-to-cloud ERP conversion project take?',
        answer: 'Most standard conversions take 2 to 3 weeks. We clean and migrate your existing historical Excel sheets, configure PostgreSQL/MySQL relational databases, set up staff roles, and train your team in Nairobi or across branches.',
      },
      {
        question: 'Does the custom ERP comply with KRA eTIMS requirements?',
        answer: 'Yes. We build automated KRA eTIMS API middleware that electronically validates commercial invoices with the Kenya Revenue Authority in real-time, outputting QR-coded fiscal receipts seamlessly.',
      },
      {
        question: 'Can staff access the ERP system from smartphones and home computers?',
        answer: 'Yes. Our ERP portals are responsive web applications accessible securely via browser on Android phones, iPhones, tablets, and desktop workstations with encrypted JWT sessions.',
      },
      {
        question: 'Where is our company data hosted?',
        answer: 'Your data is hosted on isolated NVMe private cloud servers with daily automated offsite backups. You maintain 100% data ownership and export capabilities at all times.',
      },
    ],
  },

  'ai-solutions': {
    path: '/solutions/ai-solutions/',
    focusKeyword: 'AI automation solutions Kenya WhatsApp business bots Nairobi',
    seoTitle: 'Enterprise AI Systems & Automation Kenya | Thinkers Arcade',
    metaDescription: 'Autonomous AI workflows, customer service WhatsApp bots, and local LLM pipelines connected directly to your existing business databases and ERP systems.',
    ogTitle: 'Enterprise AI Systems & Automation Kenya | Thinkers Arcade',
    ogDescription: 'Autonomous AI workflows, customer service WhatsApp bots, and local LLM pipelines connected directly to your existing business databases and ERP systems.',
    canonicalUrl: 'https://thinkersarcade.co.ke/solutions/ai-solutions/',
    schemaType: 'Service',
    wordCountTarget: '900-1200 words',
    faqs: [
      {
        question: 'What practical AI solutions can Thinkers Arcade build for my business?',
        answer: 'We build practical, high-ROI AI pipelines including: 1) WhatsApp Business AI customer service agents that answer product questions and process orders 24/7, 2) Optical character recognition (OCR) systems that automatically extract supplier invoices and delivery notes into your accounting database, and 3) Predictive inventory reordering models.',
      },
      {
        question: 'Can your WhatsApp AI bot understand Sheng and Kenyan English nuances?',
        answer: 'Yes. We ground our language models with domain-specific knowledge bases, Kenyan product names, and local conversational vernacular to ensure natural, accurate client engagement without hallucinations.',
      },
      {
        question: 'How does the WhatsApp AI bot handle payments?',
        answer: 'The bot provides payment prompts using your verified Paybill number (e.g., 542542) or triggers an STK push directly to the customer phone, subsequently verifying payment confirmation before finalizing dispatch orders.',
      },
      {
        question: 'Is my proprietary company data protected when using AI models?',
        answer: 'Absolutely. We enforce private enterprise APIs with zero data training retention policies, ensuring your business financials and customer records are never used to train public foundation models.',
      },
    ],
  },

  'seo-growth': {
    path: '/solutions/seo-growth/',
    focusKeyword: 'SEO services Kenya Google Maps 3-Pack ranking Nairobi',
    seoTitle: 'Technical SEO & Google Maps Ranking Kenya | Thinkers Arcade',
    metaDescription: 'Dominate commercial Google search queries and Google Maps 3-Pack across Nairobi, Mombasa, Nakuru, and Eldoret. Technical audits and verified schema graph.',
    ogTitle: 'Technical SEO & Google Maps Ranking Kenya | Thinkers Arcade',
    ogDescription: 'Dominate commercial Google search queries and Google Maps 3-Pack across Nairobi, Mombasa, Nakuru, and Eldoret. Technical audits and verified schema graph.',
    canonicalUrl: 'https://thinkersarcade.co.ke/solutions/seo-growth/',
    schemaType: 'Service',
    wordCountTarget: '1000-1300 words',
    faqs: [
      {
        question: 'How long does it take to rank on page 1 of Google in Kenya?',
        answer: 'For competitive local keywords in Nairobi (e.g., "solar installation Nairobi", "advocate near me", "auto spare parts Kenya"), technical optimization and Google Maps 3-Pack enhancements typically yield measurable top-rank movements within 4 to 8 weeks.',
      },
      {
        question: 'What is the Google Maps 3-Pack and why is it important for local Kenyan businesses?',
        answer: 'The 3-Pack is the prominent map snippet featuring the top three verified local companies displayed above regular organic search results. Over 65% of mobile searchers in Kenya click or call directly from the 3-Pack, generating immediate high-intent phone calls and WhatsApp inquiries.',
      },
      {
        question: 'What does a comprehensive SEO audit by Thinkers Arcade cover?',
        answer: 'Our audits inspect: 1) Core Web Vitals speed scores, 2) Crawlability and XML sitemaps, 3) Schema.org structured JSON-LD graphs, 4) Keyword search volume across Kenyan cities, 5) Google Business Profile optimization, and 6) High-authority local Kenyan backlink profiles.',
      },
      {
        question: 'Is SEO better than paid Google Ads for Kenyan businesses?',
        answer: 'While Google Ads provide instant traffic that ceases the moment ad spend stops, organic SEO builds permanent digital equity. Once ranked on page 1, you acquire prospective customer leads daily with zero cost-per-click advertising expenses.',
      },
    ],
  },

  cybersecurity: {
    path: '/solutions/cybersecurity/',
    focusKeyword: 'cybersecurity audits Kenya ODPC compliance penetration testing',
    seoTitle: 'Cybersecurity Audits & ODPC Compliance | Thinkers Arcade',
    metaDescription: 'Zero-trust server hardening, OWASP penetration testing, KRA eTIMS API security vaults, and comprehensive Kenya Data Protection Act (ODPC) compliance audits.',
    ogTitle: 'Cybersecurity Audits & ODPC Compliance | Thinkers Arcade',
    ogDescription: 'Zero-trust server hardening, OWASP penetration testing, KRA eTIMS API security vaults, and comprehensive Kenya Data Protection Act (ODPC) compliance audits.',
    canonicalUrl: 'https://thinkersarcade.co.ke/solutions/cybersecurity/',
    schemaType: 'Service',
    wordCountTarget: '900-1200 words',
    faqs: [
      {
        question: 'What is ODPC compliance and does my Kenyan business need it?',
        answer: 'The Office of the Data Protection Commissioner (ODPC) regulates personal data handling under the Kenya Data Protection Act, 2019. Any company collecting customer emails, phone numbers, or ID copies is legally required to implement technical data protection safeguards and register as a Data Controller or Processor.',
      },
      {
        question: 'What is included in a web application penetration test?',
        answer: 'We simulate real-world cyberattacks against your website or API to detect SQL injection, cross-site scripting (XSS), insecure direct object references, weak password policies, and outdated server packages before malicious actors exploit them.',
      },
      {
        question: 'How do you secure payment rails like Lipa na M-Pesa APIs?',
        answer: 'We implement cryptographic signature validation, SSL/TLS certificate pinning, IP whitelisting for Daraja callback webhooks, rate limiting against brute-force bot attacks, and encrypted database token vaults.',
      },
    ],
  },

  hosting: {
    path: '/hosting/',
    focusKeyword: 'NVMe web hosting Kenya cPanel LiteSpeed KIXP Nairobi',
    seoTitle: 'NVMe LiteSpeed Cloud Web Hosting Kenya | Thinkers Arcade',
    metaDescription: 'PCIe Gen4 NVMe servers, sub-0.8s TTFB, local KIXP peering in Nairobi, free SSL certificates, automated cPanel backups, and direct M-Pesa Paybill payments.',
    ogTitle: 'NVMe LiteSpeed Cloud Web Hosting Kenya | Thinkers Arcade',
    ogDescription: 'PCIe Gen4 NVMe servers, sub-0.8s TTFB, local KIXP peering in Nairobi, free SSL certificates, automated cPanel backups, and direct M-Pesa Paybill payments.',
    canonicalUrl: 'https://thinkersarcade.co.ke/hosting/',
    schemaType: 'Product',
    wordCountTarget: '1000-1300 words',
    faqs: [
      {
        question: 'How fast is hosting account activation after paying via M-Pesa Paybill?',
        answer: 'Account provisioning is automated and takes less than 5 minutes. As soon as you enter Paybill 542542 and Account 01550, your payment code is reconciled and cPanel login credentials are automatically dispatched to your email address.',
      },
      {
        question: 'What makes LiteSpeed NVMe hosting faster than traditional Apache or HDD hosting?',
        answer: 'PCIe Gen4 NVMe drives deliver over 7,000 MB/s read/write speeds (compared to 150 MB/s on older HDDs). Combined with the LiteSpeed Web Server, your WordPress or custom website handles 10x higher concurrent user traffic with near-instant page load times.',
      },
      {
        question: 'Will Thinkers Arcade migrate my website from my current web host for free?',
        answer: 'Yes. We provide 100% free migration assistance with zero downtime. Our server engineers transfer all files, MySQL databases, and email accounts from your current host.',
      },
      {
        question: 'Are professional business email accounts included with the hosting plan?',
        answer: 'Yes. You receive custom branded email addresses (e.g., info@yourcompany.co.ke, sales@yourcompany.co.ke) with spam protection and support for Outlook, iPhone, and Android devices.',
      },
    ],
  },

  domains: {
    path: '/domains/',
    focusKeyword: 'domain registration Kenya co ke domains search buy KeNIC',
    seoTitle: 'Domain Registration Kenya (.co.ke & .com) | Thinkers Arcade',
    metaDescription: 'Search and register .co.ke, .com, and .org domain names in Kenya. Instant DNS setup, KeNIC registry peering, free WHOIS privacy, and automated Paybill checkout.',
    ogTitle: 'Domain Registration Kenya (.co.ke & .com) | Thinkers Arcade',
    ogDescription: 'Search and register .co.ke, .com, and .org domain names in Kenya. Instant DNS setup, KeNIC registry peering, free WHOIS privacy, and automated Paybill checkout.',
    canonicalUrl: 'https://thinkersarcade.co.ke/domains/',
    schemaType: 'Service',
    wordCountTarget: '800-1100 words',
    faqs: [
      {
        question: 'How much does a .co.ke domain registration cost in Kenya?',
        answer: 'A .co.ke domain costs KSh 999 to KSh 1,200 per year at Thinkers Arcade. It includes free DNS management, DNSSEC security, and automated renewal reminders.',
      },
      {
        question: 'Do I need a registered Kenyan company to purchase a .co.ke domain?',
        answer: 'No. Any business, individual, startup, or organization can register a .co.ke domain without needing formal certificate of incorporation documentation.',
      },
      {
        question: 'Can I transfer my existing domain to Thinkers Arcade?',
        answer: 'Yes. Simply request the EPP transfer code from your current registrar and provide it to our team. We transfer your domain with zero DNS interruption.',
      },
    ],
  },

  pricing: {
    path: '/pricing/',
    focusKeyword: 'website development cost Kenya price packages apps software 2026',
    seoTitle: 'Official Software & Web Pricing Packages | Thinkers Arcade',
    metaDescription: 'Transparent rates for website design from KES 9,000, mobile apps, custom ERPs, and cloud hosting. Milestone-based Lipa Pole Pole plans available in Kenya.',
    ogTitle: 'Official Software & Web Pricing Packages | Thinkers Arcade',
    ogDescription: 'Transparent rates for website design from KES 9,000, mobile apps, custom ERPs, and cloud hosting. Milestone-based Lipa Pole Pole plans available in Kenya.',
    canonicalUrl: 'https://thinkersarcade.co.ke/pricing/',
    schemaType: 'CollectionPage',
    wordCountTarget: '1100-1400 words',
    faqs: [
      {
        question: 'Are there any hidden costs beyond the listed package prices?',
        answer: 'None. Every package lists all deliverables clearly. Starter websites include domain, SSL, and hosting setup. There are no surprise monthly licensing fees.',
      },
      {
        question: 'How does Lipa Pole Pole installment payment work?',
        answer: 'For projects KES 18,000 and above, you can pay a 40% initial deposit to begin design and architecture, 30% upon interactive prototype review, and 30% upon live launch.',
      },
      {
        question: 'Can I upgrade my package later as my business expands?',
        answer: 'Yes. Our modular software architecture allows you to begin with a KES 9,000 landing page and seamlessly scale into an e-commerce store or multi-branch ERP database.',
      },
    ],
  },

  store: {
    path: '/store/',
    focusKeyword: 'POS hardware Kenya barcode scanners receipt printers solar power',
    seoTitle: 'Commercial POS Hardware & Solar in Kenya | Thinkers Arcade',
    metaDescription: 'Turnkey POS machines, thermal receipt printers, 2D barcode scanners, cash drawers, and solar clean energy systems delivered countrywide with warranty.',
    ogTitle: 'Commercial POS Hardware & Solar in Kenya | Thinkers Arcade',
    ogDescription: 'Turnkey POS machines, thermal receipt printers, 2D barcode scanners, cash drawers, and solar clean energy systems delivered countrywide with warranty.',
    canonicalUrl: 'https://thinkersarcade.co.ke/store/',
    schemaType: 'Product',
    wordCountTarget: '900-1200 words',
    faqs: [
      {
        question: 'Do POS machines come with warranty in Kenya?',
        answer: 'Yes. All hardware terminals, barcode scanners, and thermal receipt printers include a 1-year replacement warranty against manufacturing defects.',
      },
      {
        question: 'How is equipment delivered outside Nairobi?',
        answer: 'We partner with vetted parcel couriers (Fargo Courier, Wells Fargo, G4S, Easy Coach) delivering to Mombasa, Nakuru, Eldoret, Meru, and Kisumu within 24 hours of payment confirmation.',
      },
      {
        question: 'Are thermal printers compatible with custom ERP software?',
        answer: 'Yes. All USB and Ethernet printers support ESC/POS command standards and connect directly to our custom ERPs and web platforms.',
      },
    ],
  },

  'case-studies': {
    path: '/case-studies/',
    focusKeyword: 'case studies software web development Kenya client portfolio',
    seoTitle: 'Live Client Case Studies & Results Kenya | Thinkers Arcade',
    metaDescription: 'Inspect 19 live verified client websites and measurable revenue outcomes across Kenya: Automotive, Clean Tech, Legal, Telecom, TVET, and Tourism sectors.',
    ogTitle: 'Live Client Case Studies & Results Kenya | Thinkers Arcade',
    ogDescription: 'Inspect 19 live verified client websites and measurable revenue outcomes across Kenya: Automotive, Clean Tech, Legal, Telecom, TVET, and Tourism sectors.',
    canonicalUrl: 'https://thinkersarcade.co.ke/case-studies/',
    schemaType: 'CollectionPage',
    wordCountTarget: '1000-1300 words',
    faqs: [
      {
        question: 'Are all 19 case studies shown on this page active live websites?',
        answer: 'Yes. Every listed case study is a live deployment operating on public domains including jaymaxauto.co.ke, shantisdesigns.co.ke, cklaw.co.ke, and sprinkvillenetworks.co.ke.',
      },
      {
        question: 'Can you build a similar custom solution for my industry vertical?',
        answer: 'Yes. We adapt verified architecture blueprints across sectors to ensure rapid turnaround times and high conversion performance.',
      },
    ],
  },

  insights: {
    path: '/insights/',
    focusKeyword: 'technical insights Core Web Vitals African tech trends engineering journal',
    seoTitle: 'African Tech Insights & Engineering Logs | Thinkers Arcade',
    metaDescription: 'Deep dives on Core Web Vitals, headless Next.js architectures, local Kenyan SEO, and African tech trends written by our senior software engineering team.',
    ogTitle: 'African Tech Insights & Engineering Logs | Thinkers Arcade',
    ogDescription: 'Deep dives on Core Web Vitals, headless Next.js architectures, local Kenyan SEO, and African tech trends written by our senior software engineering team.',
    canonicalUrl: 'https://thinkersarcade.co.ke/insights/',
    schemaType: 'CollectionPage',
    wordCountTarget: '900-1200 words',
    faqs: [
      {
        question: 'What topics does the Thinkers Arcade Engineering Journal cover?',
        answer: 'We publish technical analyses on Core Web Vitals speed optimization, Safaricom Daraja M-Pesa STK push integration, local SEO ranking in Kenya, and scaling relational PostgreSQL databases.',
      },
      {
        question: 'Can our technical team consult directly with your engineering leads?',
        answer: 'Yes. You can schedule a 45-minute working architecture diagnostic with our senior developers through our booking form or direct WhatsApp line.',
      },
    ],
  },

  industries: {
    path: '/industries/',
    focusKeyword: 'industry digital infrastructure Kenya automotive legal solar health',
    seoTitle: 'Industry Digital Infrastructure Stacks | Thinkers Arcade',
    metaDescription: 'Specialized digital architectures for Healthcare, Hospitality, NGOs, TVET Colleges, Churches, Solar Energy, and Commercial Real Estate across East Africa.',
    ogTitle: 'Industry Digital Infrastructure Stacks | Thinkers Arcade',
    ogDescription: 'Specialized digital architectures for Healthcare, Hospitality, NGOs, TVET Colleges, Churches, Solar Energy, and Commercial Real Estate across East Africa.',
    canonicalUrl: 'https://thinkersarcade.co.ke/industries/',
    schemaType: 'CollectionPage',
    wordCountTarget: '900-1200 words',
    faqs: [
      {
        question: 'How do you customize software for specific Kenyan industries?',
        answer: 'Each industry has unique regulatory and payment requirements. For healthcare, we enforce patient privacy and appointment reminders; for automotive, live parts catalogues and M-Pesa till checkouts; for legal, confidential client intake and litigation calendars.',
      },
      {
        question: 'Do you offer ongoing technical maintenance for industry systems?',
        answer: 'Yes. We provide 24/7 server monitoring, security patching, and priority bug resolution under guaranteed Service Level Agreements (SLAs).',
      },
    ],
  },

  'venture-lab': {
    path: '/venture-lab/',
    focusKeyword: 'venture lab tech co-founder studio Kenya startup MVP build',
    seoTitle: 'Startup Studio & Venture Lab in Kenya | Thinkers Arcade',
    metaDescription: 'Tech sweat-equity and engineering co-founding for high-potential African startups. Rapid MVP prototyping, architecture scoping, and seed validation in Nairobi.',
    ogTitle: 'Startup Studio & Venture Lab in Kenya | Thinkers Arcade',
    ogDescription: 'Tech sweat-equity and engineering co-founding for high-potential African startups. Rapid MVP prototyping, architecture scoping, and seed validation in Nairobi.',
    canonicalUrl: 'https://thinkersarcade.co.ke/venture-lab/',
    schemaType: 'AboutPage',
    wordCountTarget: '800-1100 words',
    faqs: [
      {
        question: 'How does the Thinkers Arcade Venture Lab partner with startup founders?',
        answer: 'We provide senior full-stack engineering, cloud architecture, and M-Pesa payment rails in exchange for hybrid sweat-equity, allowing founders to launch production-grade software without huge upfront capital.',
      },
      {
        question: 'What stage startups does Venture Lab accept?',
        answer: 'We work primarily with pre-seed and seed-stage founders who possess deep industry expertise, validated customer interest, and a clear commercial business model.',
      },
    ],
  },

  contact: {
    path: '/contact/',
    focusKeyword: 'contact Thinkers Arcade tech consultation Nairobi WhatsApp phone',
    seoTitle: 'Contact Web & Software Developers Kenya | Thinkers Arcade',
    metaDescription: 'Book a 45-minute working strategy session, request a project wireframe and quote, or reach us directly via WhatsApp (+254 114 646 341) or Nairobi phone.',
    ogTitle: 'Contact Web & Software Developers Kenya | Thinkers Arcade',
    ogDescription: 'Book a 45-minute working strategy session, request a project wireframe and quote, or reach us directly via WhatsApp (+254 114 646 341) or Nairobi phone.',
    canonicalUrl: 'https://thinkersarcade.co.ke/contact/',
    schemaType: 'ContactPage',
    wordCountTarget: '800-1000 words',
    faqs: [
      {
        question: 'How quickly does Thinkers Arcade respond to inquiries?',
        answer: 'We respond to all technical briefs and quotation requests within 2 business hours. For emergency hosting or payment inquiries, our direct WhatsApp hotline is available immediately at +254 114 646 341.',
      },
      {
        question: 'Where is Thinkers Arcade located in Nairobi?',
        answer: 'Our headquarters are located in Nairobi, Kenya, serving clients locally and across Nakuru, Thika, Meru, Mombasa, Kiambu, and nationwide with on-site and remote engineering teams.',
      },
      {
        question: 'Can you sign a Non-Disclosure Agreement (NDA) before we share our project brief?',
        answer: 'Yes. We respect client confidentiality and are happy to execute standard bilateral NDAs prior to detailed architectural scoping.',
      },
    ],
  },
  portal: {
    path: '/portal/',
    focusKeyword: 'thinkers arcade editorial publishing portal studio admin',
    seoTitle: 'Editorial & Publishing Studio | Thinkers Arcade Kenya',
    metaDescription: 'Secure administrative editorial publishing portal and content management studio for Thinkers Arcade Kenya digital infrastructure, technical articles, and logs.',
    ogTitle: 'Editorial & Publishing Studio | Thinkers Arcade Kenya',
    ogDescription: 'Secure administrative editorial publishing portal and content management studio for Thinkers Arcade Kenya digital infrastructure, technical articles, and logs.',
    canonicalUrl: 'https://thinkersarcade.co.ke/portal/',
    schemaType: 'WebSite',
    wordCountTarget: '800 words',
  },
  'kenya-nairobi': {
    path: '/kenya/nairobi/',
    focusKeyword: 'website development Nairobi web design company Nairobi custom software Kenya',
    seoTitle: 'Website Development & Custom Software Nairobi | Thinkers Arcade',
    metaDescription: 'Top-rated web design and custom Laravel software developers in Nairobi. Build high-converting, sub-second websites with M-Pesa STK push integration. Get instant quote.',
    ogTitle: 'Website Development & Custom Software Nairobi | Thinkers Arcade',
    ogDescription: 'Top-rated web design and custom Laravel software developers in Nairobi. Build high-converting, sub-second websites with M-Pesa STK push integration. Get instant quote.',
    canonicalUrl: 'https://thinkersarcade.co.ke/kenya/nairobi/',
    schemaType: 'Service',
    wordCountTarget: '1200-1500 words',
  },
  'kenya-mombasa': {
    path: '/kenya/mombasa/',
    focusKeyword: 'website design Mombasa e-commerce website development Mombasa Coast developers',
    seoTitle: 'E-Commerce & Website Design Mombasa | Thinkers Arcade Kenya',
    metaDescription: 'Launch high-speed e-commerce online shops in Mombasa with Safaricom Daraja M-Pesa STK push cashier checkout. Trusted Coast web developers in Nyali and Mombasa Island.',
    ogTitle: 'E-Commerce & Website Design Mombasa | Thinkers Arcade Kenya',
    ogDescription: 'Launch high-speed e-commerce online shops in Mombasa with Safaricom Daraja M-Pesa STK push cashier checkout. Trusted Coast web developers in Nyali and Mombasa Island.',
    canonicalUrl: 'https://thinkersarcade.co.ke/kenya/mombasa/',
    schemaType: 'Service',
    wordCountTarget: '1200-1500 words',
  },
  'kenya-nakuru': {
    path: '/kenya/nakuru/',
    focusKeyword: 'website design Nakuru web developers in Nakuru POS software Rift Valley',
    seoTitle: 'Website Design & Software Development Nakuru | Thinkers Arcade',
    metaDescription: 'Professional website design and custom POS software in Nakuru. High-speed websites with free .co.ke domain and NVMe hosting. Lipa Pole Pole milestone plans available.',
    ogTitle: 'Website Design & Software Development Nakuru | Thinkers Arcade',
    ogDescription: 'Professional website design and custom POS software in Nakuru. High-speed websites with free .co.ke domain and NVMe hosting. Lipa Pole Pole milestone plans available.',
    canonicalUrl: 'https://thinkersarcade.co.ke/kenya/nakuru/',
    schemaType: 'Service',
    wordCountTarget: '1200-1500 words',
  },
  'kenya-kisumu': {
    path: '/kenya/kisumu/',
    focusKeyword: 'website design Kisumu software development Kisumu app developers Nyanza',
    seoTitle: 'Custom Website Design & App Developers Kisumu | Thinkers Arcade',
    metaDescription: 'Enterprise website developers and Android/iOS app developers in Kisumu. High-converting digital infrastructure for hospitals, hotels, and retail across Nyanza.',
    ogTitle: 'Custom Website Design & App Developers Kisumu | Thinkers Arcade',
    ogDescription: 'Enterprise website developers and Android/iOS app developers in Kisumu. High-converting digital infrastructure for hospitals, hotels, and retail across Nyanza.',
    canonicalUrl: 'https://thinkersarcade.co.ke/kenya/kisumu/',
    schemaType: 'Service',
    wordCountTarget: '1200-1500 words',
  },
  'kenya-meru': {
    path: '/kenya/meru/',
    focusKeyword: 'website design Meru web development company Meru digital marketing Mt Kenya',
    seoTitle: 'Website Design & Digital Marketing Services Meru | Thinkers Arcade',
    metaDescription: 'Top web design, SEO, and social media marketing agency in Meru town. Drive Kenyan customers to your phone with fast, affordable website packages and Google local rankings.',
    ogTitle: 'Website Design & Digital Marketing Services Meru | Thinkers Arcade',
    ogDescription: 'Top web design, SEO, and social media marketing agency in Meru town. Drive Kenyan customers to your phone with fast, affordable website packages and Google local rankings.',
    canonicalUrl: 'https://thinkersarcade.co.ke/kenya/meru/',
    schemaType: 'Service',
    wordCountTarget: '1200-1500 words',
  },
};

/**
 * Resolves current view from browser URL pathname, hash or query
 */
export function getViewFromPath(pathname: string): AppView {
  const cleanPath = pathname.toLowerCase().replace(/\/+$/, "") || "/";

  for (const [view, config] of Object.entries(ROUTE_MAP)) {
    const configClean = config.path.toLowerCase().replace(/\/+$/, "") || "/";
    if (configClean === cleanPath) {
      return view as AppView;
    }
  }

  // Short path aliases for user and client convenience
  if (cleanPath === '/portal' || cleanPath === '/admin' || cleanPath === '/editorial-portal' || cleanPath === '/publisher-portal' || cleanPath === '/publisher' || cleanPath === '/editor' || cleanPath === '/login') return 'portal';
  if (cleanPath === '/website-development' || cleanPath === '/websites' || cleanPath === '/web-development') return 'website-development';
  if (cleanPath === '/app-development' || cleanPath === '/apps' || cleanPath === '/mobile-apps') return 'app-development';
  if (cleanPath === '/custom-erps' || cleanPath === '/erps' || cleanPath === '/software') return 'custom-erps';
  if (cleanPath === '/ai-solutions' || cleanPath === '/ai' || cleanPath === '/automation') return 'ai-solutions';
  if (cleanPath === '/seo-growth' || cleanPath === '/seo' || cleanPath === '/rankings') return 'seo-growth';
  if (cleanPath === '/cybersecurity' || cleanPath === '/security') return 'cybersecurity';
  if (cleanPath === '/blog' || cleanPath === '/journal') return 'insights';
  if (cleanPath === '/portfolio' || cleanPath === '/work' || cleanPath === '/clients') return 'case-studies';
  if (cleanPath === '/prices' || cleanPath === '/rates' || cleanPath === '/packages') return 'pricing';
  if (cleanPath === '/shop' || cleanPath === '/pos' || cleanPath === '/hardware') return 'store';
  if (cleanPath === '/kenya/nairobi' || cleanPath === '/nairobi') return 'kenya-nairobi';
  if (cleanPath === '/kenya/mombasa' || cleanPath === '/mombasa') return 'kenya-mombasa';
  if (cleanPath === '/kenya/nakuru' || cleanPath === '/nakuru') return 'kenya-nakuru';
  if (cleanPath === '/kenya/kisumu' || cleanPath === '/kisumu') return 'kenya-kisumu';
  if (cleanPath === '/kenya/meru' || cleanPath === '/meru') return 'kenya-meru';

  return 'home';
}

/**
 * Returns full canonical shareable URL for any view
 */
export function getShareUrl(view: AppView): string {
  const config = ROUTE_MAP[view] || ROUTE_MAP.home;
  const baseUrl = typeof window !== 'undefined' && window.location.origin
    ? window.location.origin
    : 'https://thinkersarcade.co.ke';
  return `${baseUrl}${config.path === '/' ? '/' : config.path}`;
}

/**
 * Updates browser URL bar, document title, Yoast-style meta tags,
 * and injects dynamic Schema.org JSON-LD graph.
 */
export function pushPermalink(view: AppView) {
  if (typeof window === 'undefined') return;

  const config = ROUTE_MAP[view] || ROUTE_MAP.home;
  const currentPath = window.location.pathname.replace(/\/+$/, '') || '/';
  const targetPath = config.path.replace(/\/+$/, '') || '/';

  if (currentPath !== targetPath) {
    try {
      window.history.pushState({ view }, '', config.path);
    } catch {
      // In sandboxed iframes where pushState is restricted, update hash fallback
      try {
        if (config.path !== '/') {
          window.location.hash = config.path.replace(/^\//, '');
        } else {
          window.location.hash = '';
        }
      } catch {}
    }
  }

  // 1. Update Document Title
  document.title = config.seoTitle;

  // 2. Update Meta Description
  let metaDesc = document.querySelector('meta[name="description"]');
  if (!metaDesc) {
    metaDesc = document.createElement('meta');
    metaDesc.setAttribute('name', 'description');
    document.head.appendChild(metaDesc);
  }
  metaDesc.setAttribute('content', config.metaDescription);

  // 3. Update Focus Keywords
  let metaKeywords = document.querySelector('meta[name="keywords"]');
  if (!metaKeywords) {
    metaKeywords = document.createElement('meta');
    metaKeywords.setAttribute('name', 'keywords');
    document.head.appendChild(metaKeywords);
  }
  metaKeywords.setAttribute('content', config.focusKeyword);

  // 4. Update OpenGraph Tags
  const updateMetaProperty = (prop: string, val: string) => {
    let el = document.querySelector(`meta[property="${prop}"]`);
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute('property', prop);
      document.head.appendChild(el);
    }
    el.setAttribute('content', val);
  };

  updateMetaProperty('og:title', config.ogTitle);
  updateMetaProperty('og:description', config.ogDescription);
  updateMetaProperty('og:url', config.canonicalUrl);
  updateMetaProperty('og:type', config.schemaType === 'Product' ? 'product' : 'website');
  updateMetaProperty('og:locale', 'en_KE');
  updateMetaProperty('og:site_name', 'Thinkers Arcade');
  updateMetaProperty('og:image', 'https://thinkersarcade.co.ke/thinkers-arcade-logo.webp');
  updateMetaProperty('og:image:secure_url', 'https://thinkersarcade.co.ke/thinkers-arcade-logo.webp');
  updateMetaProperty('og:image:width', '1200');
  updateMetaProperty('og:image:height', '630');
  updateMetaProperty('og:image:alt', `${config.seoTitle} | Thinkers Arcade Kenya`);
  updateMetaProperty('og:image:type', 'image/webp');

  // 5. Update Twitter Cards
  const updateMetaName = (name: string, val: string) => {
    let el = document.querySelector(`meta[name="${name}"]`);
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute('name', name);
      document.head.appendChild(el);
    }
    el.setAttribute('content', val);
  };

  updateMetaName('twitter:card', 'summary_large_image');
  updateMetaName('twitter:title', config.seoTitle);
  updateMetaName('twitter:description', config.metaDescription);
  updateMetaName('twitter:image', 'https://thinkersarcade.co.ke/thinkers-arcade-logo.webp');
  updateMetaName('twitter:image:alt', `${config.seoTitle} | Thinkers Arcade Kenya`);

  // 6. Update Canonical Link
  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  canonical.setAttribute('href', config.canonicalUrl);

  // 7. Dynamic Schema.org JSON-LD Injection (FAQPage + Breadcrumbs)
  try {
    let scriptTag = document.getElementById('dynamic-page-schema') as HTMLScriptElement | null;
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.id = 'dynamic-page-schema';
      scriptTag.type = 'application/ld+json';
      document.head.appendChild(scriptTag);
    }

    const schemaGraph: any[] = [
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: 'https://thinkersarcade.co.ke/',
          },
          ...(config.path !== '/'
            ? [
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: config.seoTitle.split('|')[0].trim(),
                  item: config.canonicalUrl,
                },
              ]
            : []),
        ],
      },
    ];

    // Append FAQPage Schema if page has FAQs (all non-home pages)
    if (config.faqs && config.faqs.length > 0) {
      schemaGraph.push({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: config.faqs.map((f) => ({
          '@type': 'Question',
          name: f.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: f.answer,
          },
        })),
      });
    }

    scriptTag.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': schemaGraph,
    });
  } catch (e) {
    console.error('Schema JSON-LD update error:', e);
  }
}
